<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import type { ClassValue, HTMLAttributes } from "svelte/elements";
	import chevronIcon from "@core/assets/icons/chevron.svg?raw";

	const id = $props.id();
	let {
		/* eslint-disable prefer-const */
		options,
		/* eslint-enable prefer-const */
		value = $bindable<string>(options[0]?.value),
		/* eslint-disable prefer-const */
		wrapperClass,
		...rest
		/* eslint-enable prefer-const */
	}: HTMLAttributes<HTMLSelectElement> & {
		options: { value: string; label: string; disabled?: boolean; class?: ClassValue }[];
		wrapperClass?: ClassValue;
		onvaluechange?(this: void, value: string): void;
		value?: string;
		name?: string;
		"aria-label": string; // Make required because of ARIA
	} = $props();
</script>

<div class={[wrapperClass, "relative text-fcrdarkgray"]}>
	<select
		bind:value
		{...rest}
		class={[
			"border border-[#999]/24 rounded-full bg-fcrlightgray-200 enabled:cursor-pointer",
			"text-ellipsis line-clamp-1 min-w-27 max-w-32 pl-4 pr-14",
			"text-sm/[3rem] placeholder:text-fcrgray appearance-none !outline-none disabled:opacity-100",
			"w-full disabled:text-fcrgray font-medium",
			value === "" && "!text-white",
			rest.class,
		]}
	>
		{#each options as { label, ...props } (props.value)}
			<option {...props}>
				{label}
			</option>
		{/each}
	</select>
	<Icon
		icon={chevronIcon}
		class="size-6 pointer-events-none [:open+&]:rotate-180 [:open+&]:text-fcrbrown transition-all absolute right-3 top-1/2 -translate-y-1/2"
	/>
</div>

<style lang="postcss">
	/*
	 * When the browser supports the base-select appearance, make the <select> fits the overall design.
	 * By 2026 we're not expecting most browsers to support this feature, so by @supports we fallback to the native look.
	 */
	@supports (appearance: base-select) {
		select,
		::picker(select) {
			appearance: base-select;
		}

		select::picker-icon {
			display: none;
		}

		::picker(select) {
			border: inherit;
			border-radius: 1.5rem;
			top: calc(anchor(bottom) + 0.375rem);
			left: anchor(left);
			padding: 0.5rem 0;
			min-width: 12rem;
		}

		select > :global(option) {
			padding: 0.5625rem 1.5rem;
			@apply font-sans text-lg/none;
		}

		option::checkmark {
			display: none;
		}

		select > :global(option:disabled::checkmark) {
			visibility: hidden;
		}

		select > :global(option:enabled:hover) {
			@apply bg-white text-fcrbrown;
		}

		select > :global(option:enabled) {
			@apply text-fcrdarkgray transition-colors;
		}

		select > :global(option:disabled) {
			@apply text-fcrgray;
		}

		select > :global(option:checked) {
			@apply text-fcrbrown;
		}
	}
</style>
